import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import { useCreateChatMutation } from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useMe from '@hooks/useMe';
import { useAppNavigation } from '@navigation/useAppNavigation';
import theme from '@themes/theme';

type ActionItem = {
  iconName: 'MessageSquare' | 'User' | 'BookOpen';
  getTitle: (ownerName?: string) => string;
  descKey: string;
  onPress: () => void;
};

type TaskRequestSentGuidanceCardProps = {
  taskOwnerName?: string;
  taskOwnerId?: string;
  onNavigateToAccount?: () => void;
  onNavigateToProjects?: () => void;
};

export const TaskRequestSentGuidanceCard: React.FC<
  TaskRequestSentGuidanceCardProps
> = ({
  taskOwnerName,
  taskOwnerId,
  onNavigateToAccount,
  onNavigateToProjects,
}) => {
  const { t } = useTranslation('models');
  const { me } = useMe();

  const showCompleteProfile = !me?.phoneNumber?.trim();

  const flows = me?.completedWebTutorialFlows ?? [];
  const showGetFamiliar = !(
    flows.includes('project_creation') &&
    flows.includes('task_creation') &&
    flows.includes('invite_member') &&
    flows.includes('chat') &&
    (flows.includes('tag_on_chat_panel_open') ||
      flows.includes('tag_on_chat_panel_closed'))
  );

  const {
    chatExpanded,
    chatListOpen,
    setChatListOpen,
    setShouldChatListOpen,
    setIsTaskWebPanelOpen,
  } = useWebDrawer();
  const { navigateToChatDrawer } = useAppNavigation();
  const { setIsOpenChat } = useActiveChat();
  const [messagingOwner, setMessagingOwner] = useState(false);

  const [createChat, { data: createChatData, error: createChatError }] =
    useCreateChatMutation();

  useEffect(() => {
    if (!createChatData) return;
    const chat = createChatData.createChat;
    setMessagingOwner(false);
    if (chat?.id) {
      setIsTaskWebPanelOpen(false);
      if (!chatExpanded && chatListOpen) {
        setShouldChatListOpen(true);
        setChatListOpen(false);
      }
      setIsOpenChat(true);
      navigateToChatDrawer({
        screen: ChatDrawerScreen.details,
        activeChat: chat as any,
      });
    }
  }, [createChatData]);

  useEffect(() => {
    if (!createChatError) return;
    setMessagingOwner(false);
  }, [createChatError]);

  const handleMessageOwner = () => {
    if (!taskOwnerId || messagingOwner) return;
    setMessagingOwner(true);
    createChat({
      variables: {
        attributes: {
          name: taskOwnerName || '',
          userIds: [taskOwnerId],
        },
      },
    });
  };

  const ALL_ACTION_ITEMS: (ActionItem & { visible: boolean })[] = [
    {
      iconName: 'MessageSquare',
      getTitle: (name) =>
        name
          ? t(
              'onBoarding.taskWelcome.requestSentGuidance.action1TitleWithName',
              { ownerName: name }
            )
          : t(
              'onBoarding.taskWelcome.requestSentGuidance.action1TitleFallback'
            ),
      descKey: 'onBoarding.taskWelcome.requestSentGuidance.action1Desc',
      onPress: handleMessageOwner,
      visible: true,
    },
    {
      iconName: 'User',
      getTitle: () =>
        t('onBoarding.taskWelcome.requestSentGuidance.action2Title'),
      descKey: 'onBoarding.taskWelcome.requestSentGuidance.action2Desc',
      onPress: () => onNavigateToAccount?.(),
      visible: showCompleteProfile,
    },
    {
      iconName: 'BookOpen',
      getTitle: () =>
        t('onBoarding.taskWelcome.requestSentGuidance.action3Title'),
      descKey: 'onBoarding.taskWelcome.requestSentGuidance.action3Desc',
      onPress: () => onNavigateToProjects?.(),
      visible: showGetFamiliar,
    },
  ];

  const ACTION_ITEMS = ALL_ACTION_ITEMS.filter((item) => item.visible);

  return (
    <Box
      backgroundColor='grey01'
      borderRadius='s'
      mb='l'
      style={{ overflow: 'hidden' }}>
      {/* Green header */}
      <Box
        backgroundColor='greenSecondaryMild'
        flexDirection='row'
        alignItems='center'
        p='m'
        style={{ gap: theme.spacing.s }}>
        <Box
          width={40}
          height={40}
          backgroundColor='greenSecondary'
          alignItems='center'
          justifyContent='center'
          style={{ borderRadius: 100 }}>
          <Icon name='Check' width={24} height={24} color='white' />
        </Box>
        <Box flex={1}>
          <Text variant='labelEmphasized' color='textPrimary'>
            {t('onBoarding.taskWelcome.requestSentGuidance.header')}
          </Text>
          <Text variant='webBodyHintSubline' color='textSecondary'>
            {t('onBoarding.taskWelcome.requestSentGuidance.headerSub')}
          </Text>
        </Box>
      </Box>

      {/* Content */}
      <Box p='m' style={{ gap: theme.spacing.s }}>
        <Text variant='labelEmphasized' color='textPrimary' mb='xxs'>
          {t('onBoarding.taskWelcome.requestSentGuidance.subheader')}
        </Text>

        {ACTION_ITEMS.map((item) => {
          const isMessageRow = item.iconName === 'MessageSquare';
          const isLoading = isMessageRow && messagingOwner;
          return (
            <TouchableOpacity
              key={item.iconName}
              onPress={item.onPress}
              disabled={isLoading}>
              <Box
                backgroundColor='white'
                borderRadius='s'
                borderWidth={1}
                borderColor='grey02'
                flexDirection='row'
                alignItems='center'
                p='m'
                style={{ gap: theme.spacing.s, opacity: isLoading ? 0.6 : 1 }}>
                <Box
                  width={40}
                  height={40}
                  backgroundColor='greenSecondaryMild'
                  alignItems='center'
                  justifyContent='center'
                  borderRadius='s'>
                  <Icon
                    name={item.iconName}
                    width={20}
                    height={20}
                    color='greenSecondary'
                  />
                </Box>
                <Box flex={1}>
                  <Text variant='labelSmall' color='textPrimary' mb='xxxs'>
                    {item.getTitle(taskOwnerName)}
                  </Text>
                  <Text variant='webBodyHintSubline' color='textSecondary'>
                    {t(item.descKey)}
                  </Text>
                </Box>
                {isLoading ? (
                  <Box
                    width={40}
                    height={40}
                    alignItems='center'
                    justifyContent='center'>
                    <ActivityIndicatorLoading />
                  </Box>
                ) : (
                  <Icon
                    name='ChevronRight'
                    width={24}
                    height={24}
                    color='grey04'
                  />
                )}
              </Box>
            </TouchableOpacity>
          );
        })}
      </Box>
    </Box>
  );
};
